import { useTranslation } from "react-i18next";
import { AlertTriangle } from "lucide-react";
import { usePublisherStatus } from "@/lib/schedule";

/**
 * Says so when scheduled posts are not going out.
 *
 * Written after a post sat at "Scheduled" for two hours past its time with no
 * error anywhere, because the publishing worker had never been started. Every
 * other failure in this system writes a readable message onto the post; that
 * one wrote nothing, because no code ran. This is the screen asking instead of
 * waiting to be told.
 *
 * Two conditions, and the wording differs because the remedy does:
 *
 *   - the worker is not running. Says what is wrong and that nothing will go
 *     out until it is back.
 *   - the worker IS running but posts are still overdue. A different and
 *     worse problem — something is failing per-post — so it points at the
 *     posts rather than at the process.
 *
 * Silent when the publisher is healthy and nothing is late, which is almost
 * always. A banner that is usually on is a banner nobody reads.
 */
export function PublisherAlert() {
  const { t } = useTranslation();
  const status = usePublisherStatus();
  const data = status.data;

  if (!data) return null;
  if (data.running && data.overdue === 0) return null;

  const body = !data.running
    ? t("publisher.stopped")
    : t("publisher.overdue", { count: data.overdue });

  return (
    <div
      role="status"
      className="flex items-start gap-3 rounded-xl border border-warning/40 bg-warning/10 px-4 py-3"
    >
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning" aria-hidden />
      <div className="space-y-1 text-sm">
        <p className="font-medium text-warning">{t("publisher.title")}</p>
        <p className="text-muted">{body}</p>
        {data.overdue > 0 && !data.running ? (
          <p className="text-xs text-muted">{t("publisher.waiting", { count: data.overdue })}</p>
        ) : null}
      </div>
    </div>
  );
}
